import React, { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useMessages } from "@/context/MessagesContext";
import IslandChat from "@/components/island/IslandChat";
import Meetings from "@/components/island/Meetings";
import Gallery from "@/components/island/Gallery";
import Dues from "@/components/island/Dues";
import CallFrame from "@/components/island/CallFrame";
import HeroBanner from "@/components/HeroBanner";
import { ISLAND_HERO, ISLAND_ICON } from "@/lib/brandImages";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Lock, MessageSquare, CalendarClock, Images, Coins } from "lucide-react";
import { toast } from "sonner";

const FRIGATE_EMOJI = { Cherokee: "🏹", Cumberland: "⚓", Pelican: "🕊️" };

function FrigateIsland({ frigate, incoming, callsEnabled, onStartCall, sendEvent }) {
  return (
    <Tabs defaultValue="chat">
      <TabsList className="glass mb-4">
        <TabsTrigger value="chat" data-testid={`island-${frigate}-chat`}><MessageSquare className="mr-1.5 h-4 w-4" />{frigate} Mess</TabsTrigger>
        <TabsTrigger value="meetings" data-testid={`island-${frigate}-meetings`}><CalendarClock className="mr-1.5 h-4 w-4" />Meetings</TabsTrigger>
        <TabsTrigger value="gallery" data-testid={`island-${frigate}-gallery`}><Images className="mr-1.5 h-4 w-4" />Gallery</TabsTrigger>
        <TabsTrigger value="dues" data-testid={`island-${frigate}-dues`}><Coins className="mr-1.5 h-4 w-4" />Dues</TabsTrigger>
      </TabsList>
      <TabsContent value="chat"><IslandChat frigate={frigate} incoming={incoming} callsEnabled={callsEnabled} onStartCall={onStartCall} sendEvent={sendEvent} /></TabsContent>
      <TabsContent value="meetings"><Meetings frigate={frigate} incoming={incoming} callsEnabled={callsEnabled} onCall={(m) => onStartCall(null, true, m)} /></TabsContent>
      <TabsContent value="gallery"><Gallery frigate={frigate} /></TabsContent>
      <TabsContent value="dues"><Dues frigate={frigate} /></TabsContent>
    </Tabs>
  );
}

export default function Island() {
  const { user } = useAuth();
  const { incoming, sendEvent } = useMessages();
  const [frigates, setFrigates] = useState([]);
  const [callsEnabled, setCallsEnabled] = useState(false);
  const [call, setCall] = useState(null);

  useEffect(() => {
    api.get("/frigates").then((r) => setFrigates(r.data.frigates));
    api.get("/island/calls/status").then((r) => setCallsEnabled(r.data.enabled)).catch(() => {});
  }, []);

  const startCall = async (conv, video, meeting) => {
    if (!callsEnabled) { toast.info("Voice & video calls are coming soon — the calling service is not connected yet."); return; }
    try {
      if (meeting?.call_room) { const { data } = await api.post(`/island/calls/${meeting.call_room}/token`); setCall(data); return; }
      const { data } = await api.post("/island/calls", { conversation_id: conv?.id || null, meeting_id: meeting?.id || null, video });
      setCall(data);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const isGuest = user?.role === "guest";
  if (isGuest) {
    return (
      <div data-testid="island-guest">
        <HeroBanner image={ISLAND_HERO} icon={ISLAND_ICON} eyebrow="Visiting brothers" title="The Island · BAN USA" description="Your branch room on the Island — chat with the brothers of BAN USA and the Galleon." testid="island-hero" />
        <IslandChat frigate={null} incoming={incoming} callsEnabled={false} onStartCall={startCall} sendEvent={sendEvent} />
      </div>
    );
  }

  return (
    <div>
      <HeroBanner image={ISLAND_HERO} icon={ISLAND_ICON} eyebrow="Where the brotherhood gathers" title="The Island" description="Galleon Mess for everyone, a private mess for each frigate — chat, groups, direct messages, meetings and the frigate gallery." testid="island-hero" />
      <Tabs defaultValue="galleon">
        <TabsList className="glass mb-6 h-auto flex-wrap" data-testid="island-tabs">
          <TabsTrigger value="galleon" className="px-5 py-2.5 text-base" data-testid="island-tab-galleon"><img src={ISLAND_ICON} alt="" className="mr-2 h-5 w-5 rounded-full object-cover" />Galleon Mess</TabsTrigger>
          {frigates.map((f) => (
            <TabsTrigger key={f.name} value={f.name} disabled={!f.is_member} className="px-5 py-2.5 text-base" data-testid={`island-tab-${f.name}`}>
              <span className="mr-2">{FRIGATE_EMOJI[f.name]}</span>{f.name}
              {!f.is_member && <Lock className="ml-2 h-3.5 w-3.5" />}
              {user?.frigate === f.name && <Badge className="ml-2 bg-[hsl(var(--gold))] text-[0.6rem] text-[hsl(var(--primary-foreground))]">Mine</Badge>}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="galleon">
          <Tabs defaultValue="chat">
            <TabsList className="glass mb-4">
              <TabsTrigger value="chat" data-testid="island-galleon-chat"><MessageSquare className="mr-1.5 h-4 w-4" />Galleon Mess</TabsTrigger>
              <TabsTrigger value="meetings" data-testid="island-galleon-meetings"><CalendarClock className="mr-1.5 h-4 w-4" />Galleon Meetings</TabsTrigger>
              <TabsTrigger value="dues" data-testid="island-galleon-dues"><Coins className="mr-1.5 h-4 w-4" />Dues</TabsTrigger>
            </TabsList>
            <TabsContent value="chat"><IslandChat frigate={null} incoming={incoming} callsEnabled={callsEnabled} onStartCall={startCall} sendEvent={sendEvent} /></TabsContent>
            <TabsContent value="meetings"><Meetings frigate={null} incoming={incoming} callsEnabled={callsEnabled} onCall={(m) => startCall(null, true, m)} /></TabsContent>
            <TabsContent value="dues"><Dues frigate={null} /></TabsContent>
          </Tabs>
        </TabsContent>
        {frigates.filter((f) => f.is_member).map((f) => (
          <TabsContent key={f.name} value={f.name}><FrigateIsland frigate={f.name} incoming={incoming} callsEnabled={callsEnabled} onStartCall={startCall} sendEvent={sendEvent} /></TabsContent>
        ))}
      </Tabs>
      {call && <CallFrame roomUrl={call.room_url} token={call.token} onLeave={() => setCall(null)} />}
    </div>
  );
}
